import { Button, Card, InputRef, message, notification, Space, Spin, Table, TableColumnType, Tag } from "antd";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../../../utils/constants";
import { CheckOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { FilterDropdownProps, TablePaginationConfig } from "antd/es/table/interface";
import { SmartButtons, SmartButtonsIndex } from "../../../utils/interfaces";
import Search from "antd/es/input/Search";
import DeleteModal from "../../GlobalComponents/Modals/DeleteModal";
import { useForm } from "react-hook-form";
import AddSingleButtonModal from "../../../Components/SmartButtonsPageComp/Modals/AddSingleModalModal";
import UploadSmartButtonsModal from "../../../Components/SmartButtonsPageComp/Modals/UploadSmartButtonsModal";
import { useHasAction } from "../../../hooks/useHasAction";
import styles from '../../../global.module.css'

const SmartButtonView: React.FC = () => {

  const canUploadList = useHasAction("smart_buttons_upload_list");
  const canAddSingle = useHasAction("smart_buttons_add_single");
  const canDelete = useHasAction("smart_buttons_delete");

  const [loading, setLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<SmartButtons[]>([]);
  const [loadingButtons, setLoadingButtons] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const searchInput = useRef<InputRef>(null);

  // Tracks whether any column filter is currently applied - antd's Table
  // reports this via onChange regardless of which mechanism (filterDropdown
  // search box or a plain `filters` list) drove it. Drives the filter-chips
  // row in the card title (see filterChips below).
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const handleTableChange = (_pagination: TablePaginationConfig, filters: Record<string, any>) => {
    setActiveFilters(filters);
  };

  const [deleteButton, setdeleteButton] = useState<number| null>(null);

  const [api, contextHolder] = notification.useNotification();

  // antd's virtual Table needs a concrete pixel height (rc-virtual-list uses
  // it to compute how many rows to render) - a CSS `calc()` string works for
  // a plain scroll container but not here, so track the equivalent number.
  const [tableScrollY, setTableScrollY] = useState(() => window.innerHeight - 255);
  useEffect(() => {
    const onResize = () => setTableScrollY(window.innerHeight - 255);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const {
    control: AddButtonControl,
    handleSubmit: AddButtonHandleSubmit,
    formState: { errors: AddButtonErrors, isValid: AddButtonIsValid },
    getValues: AddButtonGetValues,
    reset: AddButtonReset
  } = useForm<SmartButtons>({mode: "onChange"});

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: SmartButtonsIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void, close: () => void) => {
    clearFilters();
    setSearchText("");

    close();
  };

  // One color per filterable column - used both for that column's chips in
  // the title bar and for the little dot next to the column's own header,
  // so a chip and the header it came from are visually traceable at a
  // glance. Same scheme as the other list pages' filter bars.
  const COLUMN_FILTER_COLORS: Record<string, string> = {
    button_sn: "#1677ff",
    button_mac: "#722ed1",
    connected_canny: "#13c2c2",
    paired: "#faad14",
  };

  const COLUMN_FILTER_LABELS: Record<string, string> = {
    button_sn: "Button SN",
    button_mac: "Button MAC",
    connected_canny: "Connected Canny",
    paired: "Paired",
  };

  // Small colored dot appended to a column's title while it has an active
  // filter - same color as that column's chips in the title bar.
  const renderColumnTitle = (key: string, label: string) => (
    <Space size={6}>
      <span>{label}</span>
      {Array.isArray(activeFilters[key]) && activeFilters[key].length > 0 && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            display: "inline-block",
            background: COLUMN_FILTER_COLORS[key],
          }}
        />
      )}
    </Space>
  );

  // Drops one value from one column's filter, leaving any other selected
  // values on that same column untouched.
  const removeFilterValue = (key: string, value: any) => {
    setActiveFilters((prev) => {
      const current = prev[key];
      if (!Array.isArray(current)) return prev;
      const next = current.filter((v) => v !== value);
      const updated = { ...prev };
      if (next.length > 0) {
        updated[key] = next;
      } else {
        delete updated[key];
      }
      return updated;
    });
    if (searchedColumn === key) {
      setSearchText("");
      setSearchedColumn("");
    }
  };

  // Every currently applied column filter, as one flat list of removable
  // chips - shown in the card title (see the filter-chips row below).
  const filterChips = useMemo(() => {
    const chips: { key: string; label: string; color: string; onRemove: () => void }[] = [];
    Object.entries(activeFilters).forEach(([key, values]) => {
      if (!Array.isArray(values)) return;
      values.forEach((value) => {
        const displayValue = key === "paired" ? (value ? "Yes" : "No") : value;
        chips.push({
          key: `${key}-${value}`,
          label: `${COLUMN_FILTER_LABELS[key] ?? key}: ${displayValue}`,
          color: COLUMN_FILTER_COLORS[key] ?? "default",
          onRemove: () => removeFilterValue(key, value),
        });
      });
    });
    return chips;
  }, [activeFilters, searchedColumn]);// eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () => {
    setActiveFilters({});
    setSearchText("");
    setSearchedColumn("");
  };

  const getColumnSearchProps = (
    dataIndex: SmartButtonsIndex
  ): TableColumnType<SmartButtons> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Search
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onSearch={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "flex" }}
          allowClear={{
            clearIcon: (
              <CloseOutlined
                onClick={() => clearFilters && handleReset(clearFilters, close)}
              />
            ),
          }}
        />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <>
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      </>
    ),
    // Controlled so the filter-chips row (see filterChips) can clear this
    // column's search from outside the column's own dropdown.
    filteredValue: activeFilters[dataIndex as string] ?? null,
    onFilter: (value, record) => {
      const fieldValue = record[dataIndex];
      if (fieldValue !== undefined && fieldValue !== null) {
        return fieldValue
          .toString()
          .toLowerCase()
          .includes((value as string).toLowerCase());
      } else {
        return false;
      }
    },

    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const tableColumns = [
    {
      title: renderColumnTitle("button_sn", "Button SN"),
      dataIndex: "button_sn",
      key: "button_sn",
      width: 160,
      ...getColumnSearchProps("button_sn")
    },
    {
      title: renderColumnTitle("button_mac", "Button MAC"),
      dataIndex: "button_mac",
      key: "button_mac",
      width: 160,
      ...getColumnSearchProps("button_mac")
    },
    {
      title: renderColumnTitle("connected_canny", "Connected Canny"),
      dataIndex: "connected_canny",
      key: "connected_canny",
      width: 160,
      ...getColumnSearchProps("connected_canny")
    },
    {
      title: renderColumnTitle("paired", "Paired"),
      dataIndex: "paired",
      key: "paired",
      width: 100,
      render: (paired: boolean) =>
        paired ? <CheckOutlined style={{ color: "green" }} /> : <CloseOutlined style={{ color: "red" }} />,
      filters: [
        { text: "Paired", value: true },
        { text: "Not Paired", value: false },
      ],
      filteredValue: activeFilters.paired ?? null,
      onFilter: (value: boolean | React.Key, record: SmartButtons) => record.paired === value,
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id: number) => (
        <Button
          danger
          disabled={!canDelete}
          onClick={() => {
            setdeleteButton(id);
            setShowDeleteModal(true);
          }}
        >
          Delete
        </Button>
      ),
      fixed: "right" as const,
    },
  ];

  useEffect(() => {
    handleGetButtons();
  }, []);

  const handleUploadButton = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      await axios.post(`${BASE_URL}add_smart_buttons`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (err) {
      api.error({
        title: 'Upload Failed',
        description: `Failed to upload buttons.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setLoading(false);
      setShowUploadModal(false);
      await handleGetButtons();
      api.success({
        title: 'Upload Successful',
        description: `Buttons uploaded successfully.`,
        placement: 'bottomRight',
        showProgress: true
      });
    }
  };

  const handleGetButtons = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}get_smart_buttons`);
      setTableData(response.data.content.data);
    } catch (err) {
      message.error("Failed to fetche Buttons!");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteButton = async (id: number | null) => {
    if (id === null) return;
    setLoadingButtons(true);
    try {
      await axios.post(`${BASE_URL}delete_smart_button/${id}`);
      await handleGetButtons();
    } catch (err) {
      api.error({
        title: 'Delete Failed',
        description: `Failed to delete button.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setLoadingButtons(false);
      setShowDeleteModal(false);
      setdeleteButton(null);
      api.success({
        title: 'Delete Successful',
        description: `Button deleted successfully.`,
        placement: 'bottomRight',
        showProgress: true
      });
    }
  };

  const handleAddSingleButton = async () => {
    const serial = AddButtonGetValues("button_sn");
    const macAddress = AddButtonGetValues("button_mac");

    setLoading(true);
    try {
      await axios.post(`${BASE_URL}add_single_smart_button`, {
        serial_number: serial,
        bdaddr: macAddress
      });
      await handleGetButtons();
      api.success({
        title: 'Add Successful',
        description: `Button added successfully.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } catch (err: any) {
      console.log("Error adding button:", err);
      api.error({
        title: 'Failed to add button!',
        description: `Reason: ${err.response?.data?.reason || err.message}`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setLoading(false);
      setShowAddModal(false);
    }
  };

  const toggleAddModal = () => {
    setShowAddModal(!showAddModal);
    AddButtonReset();
  }

  return (
    <div>
      {contextHolder}
      <div>
        <Card
          className={styles.globalCard}
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span style={{ flexShrink: 0 }}>Smart Buttons</span>
              {filterChips.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, padding: "2px 0" }}>
                    {filterChips.map((chip) => (
                      <Tag
                        key={chip.key}
                        color={chip.color}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          chip.onRemove();
                        }}
                        style={{ flexShrink: 0 }}
                      >
                        {chip.label}
                      </Tag>
                    ))}
                  </div>
                  <Button size="small" type="link" onClick={clearAllFilters} style={{ flexShrink: 0 }}>
                    Clear all
                  </Button>
                </div>
              )}
              <span style={{ display: 'flex', gap: '5px', flexShrink: 0, marginLeft: filterChips.length > 0 ? 0 : "auto" }}>
              <Button onClick={() => setShowUploadModal(true)} type="primary" disabled={!canUploadList}>Upload Buttons LIST</Button>
              <Button onClick={toggleAddModal} type="primary" disabled={!canAddSingle}>Add Single Button</Button>
              </span>
            </div>
          }
        >
          <Spin size="large" spinning={loading}>
            <div style={{ flex: 1}}>
              <Table
                columns={tableColumns}
                dataSource={tableData}
                pagination={false}
                scroll={{x : 700, y: tableScrollY}}
                virtual
                rowKey="id"
                className='horizontally-synced-table'
                onChange={handleTableChange}
              />
            </div>
          </Spin>
        </Card>
      </div>
      <UploadSmartButtonsModal
        showUploadModal={showUploadModal}
        toggleUploadModal={() => setShowUploadModal(false)}
        onUpload={handleUploadButton}
        uploading={loading}
      />
      <AddSingleButtonModal
        showAddModal={showAddModal}
        toggleAddModal={toggleAddModal}
        handleSubmit={AddButtonHandleSubmit}
        errors={AddButtonErrors}
        onSubmit={handleAddSingleButton}
        control={AddButtonControl}
        isValid={AddButtonIsValid}
        isSubmitting={loading}
      />
      <DeleteModal
        showModal={showDeleteModal}
        onClose={() => {
          setShowDeleteModal((loadingButtons) ? true : false);
          if(!loadingButtons){
          setdeleteButton(null);
          }
        }}
        onDelete={() => handleDeleteButton(deleteButton)}
        loadingDelete={loadingButtons}
        disableDelete={loadingButtons}
        disabledCancel={loadingButtons}
        title="Are you sure you want to delete the following button?"
        content={
          <div>
            <span>Serial number:{" "}<strong>{tableData.find((item) => item.id === deleteButton)?.button_sn}</strong></span>
            <br />
            <span>Mac Address:{" "}<strong>{tableData.find((button) => button.id === deleteButton)?.button_mac}</strong></span>
          </div>
        }
      />
    </div>
  );
}

export default SmartButtonView;